// @givmo/app-admin — company-wallet deposit Idempotency-Key signature (EP-WEB6). ★ MONEY ★
//
// The series a key belongs to is held by `deposit-series.ts` (beyond the dialog, for the browser
// session, CAM-R51); this file keeps only what defines series identity: the SIGNATURE.
//
// The deposit POSTs to `POST /admin/company/wallet/deposit`, whose backend `claim_idempotency`
// dependency de-dupes ONLY across retries carrying the SAME `Idempotency-Key`. An UNKEYED retry mints
// a fresh server-side uuid4 and does NOT cross-retry-dedup — so a stable client key is the ONLY thing
// standing between a network retry / double-click and a DOUBLE CHARGE of a real company card.
//
// Series identity for a deposit = the exact wire amount PLUS the funding card PLUS the cover-fees choice
// (CAM-BE11). A different amount, a different card, or covering fees or not (a different gross) is a
// genuinely different charge → a fresh key. Retrying the SAME deposit after a maybe-charged failure
// reuses the key so the backend replays (never double-charges).

/**
 * A stable signature of the deposit being charged; a change ends the current attempt-series. The
 * discriminator is the wire amount (cents-precision decimal string, e.g. "50.00") PLUS the funding
 * card id PLUS the cover-fees choice — any of them changing is a genuinely different charge and MUST
 * get a fresh key.
 */
export interface DepositSignature {
  /** The wire amount, cents-precision decimal string (e.g. "50.00"). */
  readonly amount: string;
  /** The funding card id (a card change re-scopes the charge → a new series). */
  readonly cardId: string;
  /** Whether the deposit covers the processing fee on top (a different gross → a new series). */
  readonly coverFees: boolean;
}

/** Deterministic fingerprint of a deposit: amount + card id + the cover-fees choice. */
export function depositFingerprint(signature: DepositSignature): string {
  return `amount:${signature.amount}|card:${signature.cardId}|cover:${signature.coverFees}`;
}
